/* eslint-disable max-len */
import React from 'react';

import {ReactComponent as PeerAveragePlus} from '../../../../../assets/icons/PeerAveragePlus.svg';

import {
  EnvironmentalMetrics,
  GovernanceMetrics,
  SocialMetrics
} from './ESGMetrics';

export interface PeerBenchmarkMetricOption {
  value: string;
  label: string;
  [key: string]: unknown;
}

export interface PeerBenchmarkCompanyRow {
  name: string;
  label: string;
  value: number | string;
  detail: string;
}

export interface PeerBenchmarkMetricView {
  average: React.ReactNode;
  averageDetail: string;
  companyData: PeerBenchmarkCompanyRow[];
}

export interface PeerBenchmarkPillarRow {
  pillar: string;
  metrics: PeerBenchmarkMetricOption[];
  color: string;
  pillColor: string;
  isEnvironmental?: boolean;
  average: React.ReactNode;
  averageDetail: string;
  companyData: PeerBenchmarkCompanyRow[];
  metricData?: Record<string, PeerBenchmarkMetricView>;
}

export function createPillarRow(
  base: Omit<PeerBenchmarkPillarRow, 'metricData'>,
  metricData?: Record<string, Partial<PeerBenchmarkMetricView>>
): PeerBenchmarkPillarRow {
  if (!metricData) {
    return base;
  }

  const resolvedMetricData: Record<string, PeerBenchmarkMetricView> = {};
  for (const [key, partial] of Object.entries(metricData)) {
    resolvedMetricData[key] = {
      average: partial.average ?? base.average,
      averageDetail: partial.averageDetail ?? base.averageDetail,
      companyData: partial.companyData ?? base.companyData
    };
  }

  return {...base, metricData: resolvedMetricData};
}

export function getDefaultPeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  return [
    {
      pillar: 'Environmental',
      metrics: EnvironmentalMetrics,
      color: '#BAF6EB',
      pillColor: '#98F2E1',
      isEnvironmental: true,
      average: '706.66 tCO2e',
      averageDetail: '',
      companyData: [
        {
          name: '',
          label: 'Indian Hotels Company Ltd (Taj Hotels)',
          value: 9509080,
          detail: ''
        },
        {
          name: '',
          label: 'EIH Ltd (Oberoi Hotels)',
          value: 750,
          detail: ''
        },
        {
          name: '',
          label: 'Royal Orchid Hotels Ltd',
          value: 420,
          detail: ''
        }
        // {
        //   name: '',
        //   label: 'Indian Hotels Company Ltd (Taj Hotels)',
        //   value: 950,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'EIH Ltd (Oberoi Hotels)',
        //   value: 750,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'Royal Orchid Hotels Ltd',
        //   value: 420,
        //   detail: ''
        // }
      ]
    },
    {
      pillar: 'Social',
      metrics: SocialMetrics,
      color: '#E1F3D9',
      pillColor: '#D3EEC6',
      averageDetail: `The average board diversity includes around 18–25% women representation, 
      with 45–55% independent directors and typically 1–2 female independent directors on boards. 
      Most boards have 6–9 members, and about 30% have a dedicated ESG or diversity committee.`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'Indian Hotels Company Ltd (Taj Hotels)',
          value: '',
          detail: `The Indian Hotels Company Limited (IHCL), which owns Taj Hotels, prioritizes board diversity 
          and has a policy in place to guide its approach. This policy emphasizes the 
          importance of a diverse board, including gender diversity, and recognizes it as a key 
          element for maintaining a competitive edge. The company aims to balance various attributes 
          like skills, regional and industry experience, background, and gender to ensure a well-rounded board.`
        },
        {
          name: '',
          label: 'EIH Ltd (Oberoi Hotels)',
          value: '',
          detail: `EIH Ltd (Oberoi Hotels), which owns Taj Hotels, prioritizes board diversity 
          and has a policy in place to guide its approach. This policy emphasizes the 
          importance of a diverse board, including gender diversity, and recognizes it as a key 
          element for maintaining a competitive edge. The company aims to balance various attributes 
          like skills, regional and industry experience, background, and gender to ensure a well-rounded board.`
        },
        {
          name: '',
          label: 'Royal Orchid Hotels Ltd',
          value: '',
          detail: `Royal Orchid Hotels Ltd, which owns Taj Hotels, prioritizes board diversity 
          and has a policy in place to guide its approach. This policy emphasizes the 
          importance of a diverse board, including gender diversity, and recognizes it as a key 
          element for maintaining a competitive edge. The company aims to balance various attributes 
          like skills, regional and industry experience, background, and gender to ensure a well-rounded board.`
        }
      ]
    },
    {
      pillar: 'Governance',
      metrics: GovernanceMetrics,
      color: '#FDF6DD',
      pillColor: '#FBECB8',
      averageDetail: `Around 70–80% of companies disclose having Anti-Bribery/Anti-Corruption (ABAC) policies. 
      However, only 40–50% provide detailed procedures, such as whistleblower mechanisms or 
      third-party due diligence, in their public disclosures`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'Indian Hotels Company Ltd (Taj Hotels)',
          value: '',
          detail: `The Indian Hotels Company Limited (IHCL) has a comprehensive 
          Anti-Bribery and Anti-Corruption (ABAC) Policy that aligns with their 
          commitment to ethical conduct and integrity in all business dealings. 
          This policy aims to prevent and counter bribery and corruption by outlining 
          guiding principles and procedures. IHCL requires its personnel to recognize and report any 
          questionable transactions or behavior, and to follow established procedures.`
        },
        {
          name: '',
          label: 'EIH Ltd (Oberoi Hotels)',
          value: '',
          detail: `EIH Ltd (Oberoi Hotels) has a comprehensive 
          Anti-Bribery and Anti-Corruption (ABAC) Policy that aligns with their 
          commitment to ethical conduct and integrity in all business dealings. 
          This policy aims to prevent and counter bribery and corruption by outlining 
          guiding principles and procedures. IHCL requires its personnel to recognize and report any 
          questionable transactions or behavior, and to follow established procedures.`
        },
        {
          name: '',
          label: 'Royal Orchid Hotels Ltd',
          value: '',
          detail: `Royal Orchid Hotels Ltd has a comprehensive 
          Anti-Bribery and Anti-Corruption (ABAC) Policy that aligns with their 
          commitment to ethical conduct and integrity in all business dealings. 
          This policy aims to prevent and counter bribery and corruption by outlining 
          guiding principles and procedures. IHCL requires its personnel to recognize and report any 
          questionable transactions or behavior, and to follow established procedures.`
        }
      ]
    }
  ];
}

export function get6PeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  return [
    {
      pillar: 'Environmental',
      metrics: EnvironmentalMetrics,
      color: '#BAF6EB',
      pillColor: '#98F2E1',
      isEnvironmental: true,
      average: '359,077 tCO2e',
      averageDetail: '',
      companyData: [
        {
          name: '',
          label: 'South Indian Bank',
          value: 156242,
          detail: ''
        },
        {
          name: '',
          label: 'Karnataka Bank',
          value: 142772,
          detail: ''
        },
        {
          name: '',
          label: 'DCB Bank',
          value: 778218,
          detail: ''
        }
        // {
        //   name: '',
        //   label: 'Indian Hotels Company Ltd (Taj Hotels)',
        //   value: 950,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'EIH Ltd (Oberoi Hotels)',
        //   value: 750,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'Royal Orchid Hotels Ltd',
        //   value: 420,
        //   detail: ''
        // }
      ]
    },
    {
      pillar: 'Social',
      metrics: SocialMetrics,
      color: '#E1F3D9',
      pillColor: '#D3EEC6',
      averageDetail: '33.97% female employees',
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'South Indian Bank',
          value: '',
          detail: '46.0% female employees (4,515 of 9,814 permanent)'
        },
        {
          name: '',
          label: 'Karnataka Bank',
          value: '',
          detail: '~41.1% female employees (Approx. 3,408 of 8,286)'
        },
        {
          name: '',
          label: 'DCB Bank',
          value: '',
          detail: '14.8% female employees (1,627 of 11,012 permanent)'
        }
      ]
    },
    {
      pillar: 'Governance',
      metrics: GovernanceMetrics,
      color: '#FDF6DD',
      pillColor: '#FBECB8',
      averageDetail: `Governance is anchored at the Board level through a CSR and ESG Committee,
         chaired by a Non-Executive Director, providing strategic oversight.
          This is complemented by a quarterly Executive ESG Committee that drives implementation,
         monitors performance, and ensures alignment with organizational and regulatory objectives.`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'South Indian Bank',
          value: '',
          detail: `CSR governance includes a Board-level CSR Committee for oversight
           and an Executive ESG Committee meeting quarterly to monitor implementation,
            performance, and alignment with organizational goals.`
        },
        {
          name: '',
          label: 'Karnataka Bank',
          value: '',
          detail: `ESG matters are overseen by a dedicated Board-level committee responsible 
            for strategic direction, governance, risk oversight, and ensuring alignment
             with sustainability and compliance objectives.`
        },
        {
          name: '',
          label: 'DCB Bank',
          value: '',
          detail: `CSR and ESG matters are governed by a Board-level committee, chaired by a Non-Executive Director, 
          meeting quarterly to provide oversight, 
          strategic guidance, and performance review.`
        }
      ]
    }
  ];
}

export function get3PeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  return [
    {
      pillar: 'Environmental',
      metrics: EnvironmentalMetrics,
      color: '#BAF6EB',
      pillColor: '#98F2E1',
      isEnvironmental: true,
      average: '1,40,181.52 tCO2e',
      averageDetail: '',
      companyData: [
        {
          name: '',
          label: 'LatentView Analytics',
          value: 78382.3,
          detail: ''
        },
        {
          name: '',
          label: 'Fractal Analytics',
          value: 158772.54,
          detail: ''
        },
        {
          name: '',
          label: 'Tredence',
          value: 183389.74,
          detail: ''
        }
        // {
        //   name: '',
        //   label: 'Indian Hotels Company Ltd (Taj Hotels)',
        //   value: 950,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'EIH Ltd (Oberoi Hotels)',
        //   value: 750,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'Royal Orchid Hotels Ltd',
        //   value: 420,
        //   detail: ''
        // }
      ]
    },
    {
      pillar: 'Social',
      metrics: SocialMetrics,
      color: '#E1F3D9',
      pillColor: '#D3EEC6',
      averageDetail: '33.97% female employees',
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'LatentView Analytics',
          value: '',
          detail: '46.0% female employees (4,515 of 9,814 permanent)'
        },
        {
          name: '',
          label: 'Fractal Analytics',
          value: '',
          detail: '~41.1% female employees (Approx. 3,408 of 8,286)'
        },
        {
          name: '',
          label: 'Tredence',
          value: '',
          detail: '14.8% female employees (1,627 of 11,012 permanent)'
        }
      ]
    },
    {
      pillar: 'Governance',
      metrics: GovernanceMetrics,
      color: '#FDF6DD',
      pillColor: '#FBECB8',
      averageDetail: `Listed entities generally follow established governance practices by assigning 
      structured oversight for ESG and sustainability matters, supporting regulatory compliance,
       responsible business conduct, performance monitoring, strategic decision-making, and transparent stakeholder reporting.`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'LatentView Analytics',
          value: '',
          detail: `Board-level oversight of ESG and Sustainability is disclosed in BRSR Section B, while the CSR Committee addresses select ESG matters,
           supporting governance accountability, compliance,
            responsible business practices, and stakeholder-focused reporting.`
        },
        {
          name: '',
          label: 'Fractal Analytics',
          value: '',
          detail: `Board governance structure includes Stakeholder Relationship and CSR Committees, supporting oversight of sustainability-related matters,
           stakeholder engagement, responsible business conduct, 
           regulatory compliance, ESG priorities, and transparent corporate reporting.`
        },
        {
          name: '',
          label: 'Tredence',
          value: '',
          detail: `Board-level oversight is exercised through CSR and Stakeholder 
          Committees as per BRSR, supporting sustainability governance, ESG-related responsibilities,
           regulatory compliance, responsible business practices, stakeholder engagement, and transparent reporting.`
        }
      ]
    }
  ];
}
export function get75PeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  return [
    {
      pillar: 'Environmental',
      metrics: EnvironmentalMetrics,
      color: '#BAF6EB',
      pillColor: '#98F2E1',
      isEnvironmental: true,
      average: '1,77,48,853.33 tCO2e',
      averageDetail: '',
      companyData: [
        {
          name: '',
          label: 'JSW Steel Ltd',
          value: 53100752,
          detail: ''
        },
        {
          name: '',
          label: 'APL Apollo Tubes Ltd',
          value: 142772,
          detail: ''
        },
        {
          name: '',
          label: 'Everest Industries Ltd',
          value: 3035.99,
          detail: ''
        }
        // {
        //   name: '',
        //   label: 'Indian Hotels Company Ltd (Taj Hotels)',
        //   value: 950,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'EIH Ltd (Oberoi Hotels)',
        //   value: 750,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'Royal Orchid Hotels Ltd',
        //   value: 420,
        //   detail: ''
        // }
      ]
    },
    {
      pillar: 'Social',
      metrics: SocialMetrics,
      color: '#E1F3D9',
      pillColor: '#D3EEC6',
      averageDetail: '33.97% female employees',
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'JSW Steel Ltd',
          value: '',
          detail: '46.0% female employees (4,515 of 9,814 permanent)'
        },
        {
          name: '',
          label: 'APL Apollo Tubes Ltd',
          value: '',
          detail: '~41.1% female employees (Approx. 3,408 of 8,286)'
        },
        {
          name: '',
          label: 'Everest Industries Ltd',
          value: '',
          detail: '14.8% female employees (1,627 of 11,012 permanent)'
        }
      ]
    },
    {
      pillar: 'Governance',
      metrics: GovernanceMetrics,
      color: '#FDF6DD',
      pillColor: '#FBECB8',
      averageDetail: `Most listed companies follow standard governance practice by maintaining 
      a dedicated ESG or Sustainability Committee, ensuring focused oversight of environmental,
      social, and governance priorities, regulatory compliance, strategic sustainability initiatives,
      and transparent stakeholder reporting.`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'JSW Steel Ltd',
          value: '',
          detail: `Business Responsibility and Sustainability Reporting Committee functions as a Board sub-committee,
           chaired by an Independent Director, meeting half-yearly to oversee ESG performance,
            sustainability disclosures, governance priorities, compliance, and stakeholder accountability.`
        },
        {
          name: '',
          label: 'APL Apollo Tubes Ltd',
          value: '',
          detail: `Board-level oversight of Sustainability and ESG is disclosed in BRSR Section B,
           reflecting governance responsibility for sustainability priorities,
          regulatory alignment, ESG performance monitoring, risk management, and transparent stakeholder reporting.`
        },
        {
          name: '',
          label: 'Everest Industries Ltd',
          value: '',
          detail: `Board-level governance committee conducts regular ESG reviews as per BRSR,
           ensuring structured oversight of sustainability priorities, compliance requirements,
           ESG performance, risk management, responsible business practices, and
            transparent stakeholder accountability.`
        }
      ]
    }
  ];
}

export function get2PeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  return [
    {
      pillar: 'Environmental',
      metrics: EnvironmentalMetrics,
      color: '#BAF6EB',
      pillColor: '#98F2E1',
      isEnvironmental: true,
      average: '1,40,181.52 tCO2e',
      averageDetail: '',
      companyData: [
        {
          name: '',
          label: 'Ashoka Buildcon Ltd',
          value: 78382.3,
          detail: ''
        },
        {
          name: '',
          label: 'PNC Infratech Ltd',
          value: 158772.54,
          detail: ''
        },
        {
          name: '',
          label: 'KNR Constructions Ltd',
          value: 183389.74,
          detail: ''
        }
        // {
        //   name: '',
        //   label: 'Indian Hotels Company Ltd (Taj Hotels)',
        //   value: 950,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'EIH Ltd (Oberoi Hotels)',
        //   value: 750,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'Royal Orchid Hotels Ltd',
        //   value: 420,
        //   detail: ''
        // }
      ]
    },
    {
      pillar: 'Social',
      metrics: SocialMetrics,
      color: '#E1F3D9',
      pillColor: '#D3EEC6',
      averageDetail: '33.97% female employees',
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'Ashoka Buildcon Ltd',
          value: '',
          detail: '46.0% female employees (4,515 of 9,814 permanent)'
        },
        {
          name: '',
          label: 'PNC Infratech Ltd',
          value: '',
          detail: '~41.1% female employees (Approx. 3,408 of 8,286)'
        },
        {
          name: '',
          label: 'KNR Constructions Ltd',
          value: '',
          detail: '14.8% female employees (1,627 of 11,012 permanent)'
        }
      ]
    },
    {
      pillar: 'Governance',
      metrics: GovernanceMetrics,
      color: '#FDF6DD',
      pillColor: '#FBECB8',
      averageDetail: `Listed entities generally follow established governance practices by assigning structured
       oversight for ESG and sustainability matters, supporting regulatory compliance, responsible business conduct,
        performance monitoring, strategic decision-making, and transparent stakeholder reporting.`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'Ashoka Buildcon Ltd',
          value: '',
          detail: `Board-level oversight of ESG and Sustainability is disclosed in BRSR Section B,
           while the CSR Committee addresses select ESG matters, supporting governance accountability,
            compliance, responsible business practices, and stakeholder-focused reporting.`
        },
        {
          name: '',
          label: 'PNC Infratech Ltd',
          value: '',
          detail: `Board governance structure includes Stakeholder Relationship and CSR Committees,
           supporting oversight of sustainability-related matters, stakeholder engagement,
            responsible business conduct, regulatory compliance, ESG priorities, and transparent corporate reporting.`
        },
        {
          name: '',
          label: 'KNR Constructions Ltd',
          value: '',
          detail: `Board-level oversight is exercised through CSR and Stakeholder Committees as per BRSR,
           supporting sustainability governance, ESG-related responsibilities, regulatory compliance,
            responsible business practices, stakeholder engagement, and transparent reporting.`
        }
      ]
    }
  ];
}

export function get9PeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  return [
    {
      pillar: 'Environmental',
      metrics: EnvironmentalMetrics,
      color: '#BAF6EB',
      pillColor: '#98F2E1',
      isEnvironmental: true,
      average: '18,403 tCO2e',
      averageDetail: '',
      companyData: [
        {
          name: '',
          label: 'EPL Limited',
          value: 1601,
          detail: ''
        },
        {
          name: '',
          label: 'Huhtamaki India Limited',
          value: 10452,
          detail: ''
        },
        {
          name: '',
          label: 'TCPL Packaging Limited',
          value: 3910,
          detail: ''
        },
        {
          name: '',
          label: 'Uflex Limited',
          value: 57648,
          detail: ''
        }
        // {
        //   name: '',
        //   label: 'Indian Hotels Company Ltd (Taj Hotels)',
        //   value: 950,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'EIH Ltd (Oberoi Hotels)',
        //   value: 750,
        //   detail: ''
        // },
        // {
        //   name: '',
        //   label: 'Royal Orchid Hotels Ltd',
        //   value: 420,
        //   detail: ''
        // }
      ]
    },
    {
      pillar: 'Social',
      metrics: SocialMetrics,
      color: '#E1F3D9',
      pillColor: '#D3EEC6',
      averageDetail: `Peer companies disclose board or committee-level ESG oversight,
         indicating formal governance responsibility for sustainability-related matters.`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'EPL Limited',
          value: '',
          detail: `EPL Limited discloses ESG oversight through its Sustainability Steering Committee, 
          with the Risk Management Committee also assessing ESG-related risks as 
          part of the company’s governance framework.`
        },
        {
          name: '',
          label: 'Huhtamaki India Limited',
          value: '',
          detail: `Huhtamaki India Limited discloses ESG oversight through
             its CSR Committee, which handles ESG-related matters,
             while the Board reviews and approves the committee’s recommendations as part of the governance process.`
        },
        {
          name: '',
          label: 'TCPL Packaging Limited',
          value: '',
          detail: `TCPL Packaging Limited discloses ESG oversight through its Executive Director,
           with broader oversight from the Board and Managing Director as stated in its BRSR governance framework.`
        },
        {
          name: '',
          label: 'Uflex Limited',
          value: '',
          detail: `Uflex Limited discloses ESG oversight through its CSR Committee and Board,
           which review and discuss ESG and sustainability-related matters
            as part of the company’s governance framework.`
        }
      ]
    },
    {
      pillar: 'Governance',
      metrics: GovernanceMetrics,
      color: '#FDF6DD',
      pillColor: '#FBECB8',
      averageDetail: `Listed entities generally follow established governance practices by assigning structured
       oversight for ESG and sustainability matters, supporting regulatory compliance, responsible business conduct,
        performance monitoring, strategic decision-making, and transparent stakeholder reporting.`,
      average: <PeerAveragePlus />,
      companyData: [
        {
          name: '',
          label: 'EPL Limited',
          value: '',
          detail: `EPL Limited discloses ESG oversight through its Sustainability Steering Committee, 
          with the Risk Management Committee also assessing ESG-related risks as 
          part of the company’s governance framework.`
        },
        {
          name: '',
          label: 'Huhtamaki India Limited',
          value: '',
          detail: `Huhtamaki India Limited discloses ESG oversight through
             its CSR Committee, which handles ESG-related matters,
             while the Board reviews and approves the committee’s recommendations as part of the governance process.`
        },
        {
          name: '',
          label: 'TCPL Packaging Limited',
          value: '',
          detail: `TCPL Packaging Limited discloses ESG oversight through its Executive Director,
           with broader oversight from the Board and Managing Director as stated in its BRSR governance framework.`
        },
        {
          name: '',
          label: 'Uflex Limited',
          value: '',
          detail: `Uflex Limited discloses ESG oversight through its CSR Committee and Board,
           which review and discuss ESG and sustainability-related matters
            as part of the company’s governance framework.`
        }
      ]
    }
  ];
}
export function get83PeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  const polyMedicure = 'Poly Medicure Ltd';
  const tarsonsProducts = 'Tarsons Products Ltd';

  const socialMetric = (
    averageDetail: string,
    polyDetail: string,
    tarsonsDetail: string
  ): Partial<PeerBenchmarkMetricView> => ({
    average: <PeerAveragePlus />,
    averageDetail,
    companyData: [
      {name: '', label: polyMedicure, value: '', detail: polyDetail},
      {name: '', label: tarsonsProducts, value: '', detail: tarsonsDetail}
    ]
  });

  const envCompaniesNA = [
    {name: '', label: polyMedicure, value: 'NA', detail: ''},
    {name: '', label: tarsonsProducts, value: 'NA', detail: ''}
  ];

  return [
    createPillarRow(
      {
        pillar: 'Environmental',
        metrics: EnvironmentalMetrics,
        color: '#BAF6EB',
        pillColor: '#98F2E1',
        isEnvironmental: true,
        average: '1265.38 tCO2e',
        averageDetail: '',
        companyData: [
          {name: '', label: polyMedicure, value: 2319.85, detail: ''},
          {name: '', label: tarsonsProducts, value: 210.91, detail: ''}
        ]
      },
      {
        1: {
          average: '1265.38 tCO2e',
          averageDetail: '',
          companyData: [
            {name: '', label: polyMedicure, value: 2319.85, detail: ''},
            {name: '', label: tarsonsProducts, value: 210.91, detail: ''}
          ]
        },
        2: {
          average: '20137.435 tCO2e',
          averageDetail: '',
          companyData: [
            {name: '', label: polyMedicure, value: 33962.28, detail: ''},
            {name: '', label: tarsonsProducts, value: 6312.59, detail: ''}
          ]
        },
        3: {
          average: 'NA',
          averageDetail: '',
          companyData: [
            {name: '', label: polyMedicure, value: 'NA', detail: ''},
            {name: '', label: tarsonsProducts, value: 10417.88, detail: ''}
          ]
        },
        4: {
          average: '98.65%',
          averageDetail: '',
          companyData: [
            {name: '', label: polyMedicure, value: '97.30%', detail: ''},
            {name: '', label: tarsonsProducts, value: '100%', detail: ''}
          ]
        },
        5: {
          average: '29500.835 Kilolitres (KL)',
          averageDetail: '',
          companyData: [
            {name: '', label: polyMedicure, value: '54,325 KL', detail: ''},
            {name: '', label: tarsonsProducts, value: '4,676.67 KL', detail: ''}
          ]
        },
        6: {average: 'NA', averageDetail: '', companyData: envCompaniesNA},
        7: {average: 'NA', averageDetail: '', companyData: envCompaniesNA},
        8: {average: 'NA', averageDetail: '', companyData: envCompaniesNA}
      }
    ),
    createPillarRow(
      {
        pillar: 'Social',
        metrics: SocialMetrics,
        color: '#E1F3D9',
        pillColor: '#D3EEC6',
        average: <PeerAveragePlus />,
        averageDetail:
          'Across the Indian medical technology sector, the percentage of female permanent employees typically ranges between 8 and 22 percent, although certain assembly-line operations in this sector employ a workforce with a much higher female representation.',
        companyData: [
          {
            name: '',
            label: polyMedicure,
            value: '',
            detail:
              'Poly Medicure reported that 8.6 percent of its permanent employees were female in FY 2023-24, with 104 female employees out of a total of 1,207 permanent employees.'
          },
          {
            name: '',
            label: tarsonsProducts,
            value: '',
            detail:
              'Tarsons Products reported that 9 percent of its permanent employees were female in FY 2023-24, with 14 female employees out of a total of 150 permanent employees.'
          }
        ]
      },
      {
        1: socialMetric(
          'Across the Indian medical technology sector, the percentage of female permanent employees typically ranges between 8 and 22 percent, although certain assembly-line operations in this sector employ a workforce with a much higher female representation.',
          'Poly Medicure reported that 8.6 percent of its permanent employees were female in FY 2023-24, with 104 female employees out of a total of 1,207 permanent employees.',
          'Tarsons Products reported that 9 percent of its permanent employees were female in FY 2023-24, with 14 female employees out of a total of 150 permanent employees.'
        ),
        2: socialMetric(
          'The Securities and Exchange Board of India mandates a minimum of one woman director on the board of every listed entity, and the Peer average for female board representation currently stands at approx 16 percent.',
          'Poly Medicure had 2 female directors out of 12 total directors in FY 2023-24, representing 16.67 percent female board representation. However, this proportion decreased to approximately 9 percent in FY 2024-25 (1 of 11 directors) following retirements and new appointments to the Board.',
          'Tarsons Products had 1 female director out of 6 total directors in FY 2023-24, representing 16.67 percent female board representation.'
        ),
        3: socialMetric(
          'Peers reported 96.3% coverage on skill upgradation training for its employees.',
          'Poly Medicure reported 100 percent coverage on skill upgradation training for both its employees and its workers in its FY 2023-24 BRSR.',
          'Tarsons Products reported 92.67 percent coverage on skill upgradation training for its employees in its FY 2023-24 BRSR.'
        ),
        4: socialMetric(
          'Across the Peers, the annual employee turnover rate typically ranges between 7 and 13 percent.',
          'Poly Medicure reported a total turnover rate of 12.71 percent for its permanent employees in FY 2023-24.',
          'Tarsons Products reported a total turnover rate of 7.72 percent for its permanent employees in FY 2023-24.'
        ),
        5: socialMetric(
          'Peers reported 0% Lost Time Injury Frequency Rate for both employees and workers.',
          'Poly Medicure reported a Lost Time Injury Frequency Rate of zero for both its employees and its workers in FY 2023-24.',
          'Tarsons Products reported a Lost Time Injury Frequency Rate of zero in FY 2023-24, although two recordable work-related injuries to workers were reported during the year.'
        ),
        6: socialMetric(
          'The industry standard for human rights training coverage across the Indian medical device sector ranges between 80 and 100 percent of employees and workers, Poly Medicure being an exception.',
          'Poly Medicure reported zero percent specific human rights training coverage in FY 2023-24, although the company has a Human Rights policy and a comprehensive Code of Conduct in place.',
          'Tarsons Products reported human rights training coverage of 83.33 percent for its employees and 91.68 percent for its workers in FY 2023-24.'
        ),
        7: socialMetric(
          'Supply chain social assessment is a voluntary Leadership indicator under the BRSR framework, and mid-capitalisation peers typically assess between 5 and 30 percent of their value chain partners.',
          'Poly Medicure has limited disclosure on this metric, although a sustainable sourcing initiative is currently underway and vendor ESG audits have recently been introduced.',
          'Tarsons Products has not disclosed an exact percentage, although the company has reported that more than 80 percent of its raw material is sourced sustainably from suppliers who are certified as compliant with ESG standards.'
        )
      }
    ),
    createPillarRow(
      {
        pillar: 'Governance',
        metrics: GovernanceMetrics,
        color: '#FDF6DD',
        pillColor: '#FBECB8',
        average: <PeerAveragePlus />,
        averageDetail:
          'The peers maintain Board-level ESG, sustainability, or Corporate Social Responsibility committees responsible for overseeing ESG performance.',
        companyData: [
          {
            name: '',
            label: polyMedicure,
            value: '',
            detail:
              'Poly Medicure has a Corporate Social Responsibility Committee that is responsible for sustainability-related oversight, and the Risk Management Committee additionally covers ESG-related risks at the Board level.'
          },
          {
            name: '',
            label: tarsonsProducts,
            value: '',
            detail:
              'Tarsons Products has designated its Whole-Time Director, Mr. Aryan Sehgal, as the individual responsible for ESG strategy formulation and oversight.'
          }
        ]
      },
      {
        1: socialMetric(
          'The peers maintain Board-level ESG, sustainability, or Corporate Social Responsibility committees responsible for overseeing ESG performance.',
          'Poly Medicure has a Corporate Social Responsibility Committee that is responsible for sustainability-related oversight, and the Risk Management Committee additionally covers ESG-related risks at the Board level.',
          'Tarsons Products has designated its Whole-Time Director, Mr. Aryan Sehgal, as the individual responsible for ESG strategy formulation and oversight.'
        ),
        2: socialMetric(
          'The presence of formal anti-bribery and anti-corruption policies is a regulatory requirement and is prevalent among peers',
          'Poly Medicure has a comprehensive Code of Conduct, a Whistle-blower Policy, and a Vigil Mechanism in place, and the company reported zero bribery or corruption cases in FY 2023-24.',
          'Tarsons Products has a formal Anti-Corruption and Anti-Bribery Policy along with a Whistle-blower Policy in place, and the company reported zero bribery or corruption cases in FY 2023-24.'
        )
      }
    )
  ];
}

export function get83EmptyPeerBenchmarkData(): PeerBenchmarkPillarRow[] {
  const emptyMetric = (): Partial<PeerBenchmarkMetricView> => ({
    average: 'NA',
    averageDetail: '',
    companyData: []
  });

  return [
    createPillarRow(
      {
        pillar: 'Environmental',
        metrics: EnvironmentalMetrics,
        color: '#BAF6EB',
        pillColor: '#98F2E1',
        isEnvironmental: true,
        average: 'NA',
        averageDetail: '',
        companyData: []
      },
      {
        1: emptyMetric(),
        2: emptyMetric(),
        3: emptyMetric(),
        4: emptyMetric(),
        5: emptyMetric(),
        6: emptyMetric(),
        7: emptyMetric(),
        8: emptyMetric()
      }
    ),
    createPillarRow(
      {
        pillar: 'Social',
        metrics: SocialMetrics,
        color: '#E1F3D9',
        pillColor: '#D3EEC6',
        average: 'NA',
        averageDetail: '',
        companyData: []
      },
      {
        1: emptyMetric(),
        2: emptyMetric(),
        3: emptyMetric(),
        4: emptyMetric(),
        5: emptyMetric(),
        6: emptyMetric(),
        7: emptyMetric()
      }
    ),
    createPillarRow(
      {
        pillar: 'Governance',
        metrics: GovernanceMetrics,
        color: '#FDF6DD',
        pillColor: '#FBECB8',
        average: 'NA',
        averageDetail: '',
        companyData: []
      },
      {
        1: emptyMetric(),
        2: emptyMetric()
      }
    )
  ];
}
